/**
 * A person's notification preferences on a platform (AIOE 0.32.0; docs/workbench-integration.md section 24):
 * an optional email digest of what is open for them, quiet hours that hold the phone's buzz (never the
 * record), and whether records for them in particular count beside the ones that need them. One set
 * per person per organisation, read and written at `GET/PUT /me/notifications/v1`.
 *
 * AIOE's own contract, **mirrored by the AI Workbench** (`src/platform/notificationPreferences.ts`)
 * so its desktop quiet hours follow the platform's when the person is signed in to one: the desktop
 * reads `quietHours` and `timeZone` through {@link inQuietHours} and holds its own notifications the
 * same way. Change both sides together and bump `NOTIFICATION_PREFERENCES_VERSION` when the wire shape changes.
 *
 * - **Needs you always counts.** Only `forYou` is a choice: records addressed to the person that ask for
 *   no decision are left out of the digest and the catch-up unless it is on.
 * - **Quiet hours hold the buzz.** A push to a phone during them is kept (durably) and one catch-up
 *   push goes when they end: "While you were away: 3 things need you". The records are in the inbox the
 *   whole time; nothing is ever held back from it.
 * - **The time zone** is the person's own (IANA) or, when none is set, the organisation's. The digest's
 *   hour is read in it too.
 * - **The address** is the one on the person's verified sign-in; it is never taken from the request.
 */
import { z } from "zod";
import { timeZoneSchema, zoneOffsetMs } from "./budgets.js";
export const NOTIFICATION_PREFERENCES_VERSION = 1;
export const NOTIFICATION_PREFERENCES_PATH = "/me/notifications/v1";
export const digestCadenceSchema = z.enum(["off", "hourly", "daily"]);
/** A time of day, "HH:MM" on a 24-hour clock. */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "a time of day such as 22:00");
export const notificationPreferencesSchema = z.object({
    digest: z.object({ cadence: digestCadenceSchema, /** The local hour a daily digest goes (0 to 23). */ hour: z.number().int().min(0).max(23).default(8) }).default({ cadence: "off", hour: 8 }),
    quietHours: z.object({ enabled: z.boolean(), start: timeOfDaySchema, end: timeOfDaySchema }).default({ enabled: false, start: "22:00", end: "07:00" }),
    /** The person's own zone; unset, the organisation's. */
    timeZone: timeZoneSchema.optional(),
    /** Whether records addressed to the person that need no decision count in the digest and the catch-up (needs-you always does). */
    forYou: z.boolean().default(false),
});
export const DEFAULT_NOTIFICATION_PREFERENCES = { digest: { cadence: "off", hour: 8 }, quietHours: { enabled: false, start: "22:00", end: "07:00" }, forYou: false };
const minutesOf = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** Minutes past local midnight at an instant, in a zone. */
export function localMinutes(timeZone, at) {
    const wall = new Date(at.getTime() + zoneOffsetMs(timeZone, at));
    return wall.getUTCHours() * 60 + wall.getUTCMinutes();
}
/** Whether `at` falls in the quiet hours (the window may cross midnight; start equal to end is no window). */
export function inQuietHours(q, timeZone, at) {
    if (!q.enabled)
        return false;
    const s = minutesOf(q.start);
    const e = minutesOf(q.end);
    if (s === e)
        return false;
    const m = localMinutes(timeZone, at);
    return s < e ? m >= s && m < e : m >= s || m < e;
}
//# sourceMappingURL=notificationPreferences.js.map